/**
 * @fileoverview Unified site action taxonomy for engagement analytics.
 * @description Closed-set labels for `site_action` events so CTAs, navigation,
 * toggles, and outbound clicks group cleanly in GA4 without high-cardinality
 * element IDs in event names.
 *
 * @module lib/site-intelligence/actions
 */

/** Kind of UI interaction (closed set). */
export type SiteActionKind =
  | "cta"
  | "navigation"
  | "outbound"
  | "toggle"
  | "accordion"
  | "video"
  | "social"
  | "pricing"
  | "form"
  | "download"

/** Surface where the action occurred (closed set). */
export type SiteActionSurface =
  | "navbar"
  | "hero"
  | "footer"
  | "resource_doormat"
  | "pricing"
  | "about"
  | "blog"
  | "resources"
  | "frameworks"
  | "faq"
  | "global"
  | "unknown"

/** Normalized CTA / link labels — prefer these over free text. */
export type SiteActionLabel =
  | "request_access"
  | "pricing"
  | "frameworks"
  | "resources"
  | "blog"
  | "about"
  | "logo"
  | "buy"
  | "demo"
  | "contact"
  | "social"
  | "billing_toggle"
  | "theme"
  | "other"

/**
 * Whether an action label represents high purchase/lead intent.
 *
 * @param {SiteActionLabel} label - Normalized action label.
 * @returns {boolean} True when the action signals evaluation/conversion intent.
 */
export function isHighIntentActionLabel(label: SiteActionLabel): boolean {
  return label === "request_access" || label === "buy" || label === "demo"
}

/**
 * Maps a free-text button/link name to a closed-set action label.
 *
 * @param {string} raw - Raw label from UI (e.g. "Request Access").
 * @returns {SiteActionLabel} Normalized label for analytics.
 */
export function normalizeActionLabel(raw: string): SiteActionLabel {
  const key = raw.trim().toLowerCase()
  if (key.includes("request") && key.includes("access")) return "request_access"
  if (key.includes("pricing") || key === "price") return "pricing"
  if (key.includes("framework")) return "frameworks"
  if (key.includes("resource")) return "resources"
  if (key.includes("blog")) return "blog"
  if (key.includes("about")) return "about"
  if (key === "logo") return "logo"
  if (key.includes("buy") || key.includes("checkout") || key.includes("purchase")) return "buy"
  if (key.includes("demo")) return "demo"
  if (key.includes("contact")) return "contact"
  if (key.includes("twitter") || key.includes("linkedin") || key.includes("youtube") ||
      key.includes("github") || key.includes("reddit") || key.includes("product hunt") ||
      key.includes("pinterest")) {
    return "social"
  }
  return "other"
}

/**
 * Infers action surface from a location string passed by legacy call sites.
 *
 * @param {string} location - Legacy location hint (e.g. "Navigation", "About CTA").
 * @returns {SiteActionSurface} Normalized surface.
 */
export function surfaceFromLocation(location: string): SiteActionSurface {
  const key = location.trim().toLowerCase()
  if (key.includes("nav")) return "navbar"
  if (key.includes("hero")) return "hero"
  if (key.includes("footer")) return "footer"
  if (key.includes("pricing")) return "pricing"
  if (key.includes("about")) return "about"
  if (key.includes("blog")) return "blog"
  if (key.includes("resource")) return "resources"
  if (key.includes("framework")) return "frameworks"
  if (key.includes("faq")) return "faq"
  return "unknown"
}
